import React from "react";
import { IRentedPropertyDetail } from "../types/ownerDashboard.types";
import { useRentedPropertyPhotos } from "../hooks/useRentedPropertyPhotos";
import DashboardModal from "./DashboardModal";
import RentedPropertyPhotoCarousel from "./RentedPropertyPhotoCarousel";
import {
  PropertyListingView,
  buildPropertyListingView,
} from "../utils/rentedPropertyDisplay";

interface RentedPropertyDetailsModalProps {
  title: string;
  detail: IRentedPropertyDetail | null;
  fallbackListing?: Pick<PropertyListingView, "streetAddress" | "unit" | "areaLabel" | "description">;
  onClose: () => void;
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[7.5rem_minmax(0,1fr)] sm:gap-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-[#6e7681]">
        {label}
      </dt>
      <dd className="text-sm text-[#c9d1d9]">{value}</dd>
    </div>
  );
}

export default function RentedPropertyDetailsModal({
  title,
  detail,
  fallbackListing,
  onClose,
}: RentedPropertyDetailsModalProps) {
  const listing = detail
    ? buildPropertyListingView(detail)
    : fallbackListing ?? buildPropertyListingView(null);

  const { photos, isLoadingPhotos } = useRentedPropertyPhotos(detail);

  return (
    <DashboardModal title={title} onClose={onClose} maxWidth="lg">
      <dl className="space-y-4">
        <DetailRow label="Endereço" value={listing.streetAddress} />

        {listing.unit.apartment && (
          <DetailRow label="Apartamento" value={listing.unit.apartment} />
        )}

        {listing.unit.floor && (
          <DetailRow label="Andar" value={listing.unit.floor} />
        )}

        {listing.unit.complement && (
          <DetailRow label="Complemento" value={listing.unit.complement} />
        )}

        <DetailRow label="Metragem" value={listing.areaLabel} />

        <div className="grid gap-2 sm:grid-cols-[7.5rem_minmax(0,1fr)] sm:gap-3">
          <dt className="text-xs font-semibold uppercase tracking-wide text-[#6e7681]">
            Descrição
          </dt>
          <dd className="whitespace-pre-wrap text-sm leading-relaxed text-[#c9d1d9]">
            {listing.description}
          </dd>
        </div>
      </dl>

      {isLoadingPhotos && (
        <div
          className="mt-4 h-56 animate-pulse rounded-xl border border-white/[0.08] bg-[#0d1117] sm:h-72"
          data-testid="rented-property-photos-loading"
          aria-hidden
        />
      )}

      {!isLoadingPhotos && photos.length > 0 && (
        <RentedPropertyPhotoCarousel photos={photos} title={title} />
      )}
    </DashboardModal>
  );
}
